import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { getProject, copyProject } from '@/src/api/project'
import { useAuth } from '@/src/components/AuthContextProvider'
import { ProjectView } from '@/src/components/manage/projects/ProjectView'
import { PERMISSIONS } from '@/permission.config'
import { Button } from '@/src/components/ui/button'
import { Badge } from '@/src/components/ui/badge'
import { PencilIcon, CopyIcon, UsersIcon } from '@phosphor-icons/react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_manage/manage/projects/$id')({
  component: ProjectPage,
})

function ProjectPage() {
  const { id } = useParams({ from: '/_manage/manage/projects/$id' })
  const navigate = useNavigate()
  const { permissions, isAdmin, user } = useAuth()
  const queryClient = useQueryClient()
  
  // Check permission client-side
  useEffect(() => {
    const hasAccess = permissions.includes(PERMISSIONS.PROJECTS_MANAGE) || permissions.includes('*') || isAdmin
    if (!hasAccess) {
      navigate({ to: '/not-permitted' })
    }
  }, [permissions, isAdmin, navigate])

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', 'get', id],
    queryFn: () => getProject({ data: { id } }),
  })

  const copyMutation = useMutation({
    mutationFn: () => copyProject({ data: { id } }),
    onSuccess: (newProject) => {
      queryClient.invalidateQueries({ queryKey: ['project'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('Project copied successfully')
      navigate({ to: '/manage/projects/$id', params: { id: newProject.id }, search: {} })
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const isOwner = project?.createdById === user?.id
  const isLinkedUser = !!project?.users?.some((member: any) => member.id === user?.id)
  const isShared = (project?.users?.length ?? 0) > 0
  const canEditProject = !!project && (isOwner || isAdmin || isLinkedUser)

  return (
    <div className="flex flex-col h-full">
      <AdminToolbar>
        <AdminToolbar.Left>
          {isShared && (
            <Badge variant="outline" className="gap-1">
              <UsersIcon className="w-3.5 h-3.5" />
              Shared ({project?.users?.length ?? 0})
            </Badge>
          )}
        </AdminToolbar.Left>
        <AdminToolbar.Title className="text-lg font-semibold mb-1 md:mb-0 truncate max-w-56 sm:max-w-80 md:max-w-none">
          <span title={project?.name || 'Project'}>{project?.name || 'Project'}</span>
        </AdminToolbar.Title>
        <AdminToolbar.Right>
          {isShared && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyMutation.mutate()}
              disabled={copyMutation.isPending}
            >
              <CopyIcon className="w-4 h-4 mr-1" />
              {copyMutation.isPending ? 'Copying...' : 'Copy Project'}
            </Button>
          )}
          {(isOwner || isAdmin) && (
            <Button
              size="sm"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.location.assign(`/manage/projects/${id}/edit`)
                }
              }}
              aria-label="Edit project"
              title="Edit project"
            >
              <PencilIcon className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Edit</span>
            </Button>
          )}
        </AdminToolbar.Right>
      </AdminToolbar>
        <ProjectView 
          project={project} 
          isLoading={isLoading}
          canEdit={canEditProject}
        />
    </div>
  )
}
